import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { 
  Tag, Plus, Search, Filter, Clock, CheckCircle2, AlertTriangle, 
  Trash2, Edit3, Eye, RefreshCw, Sparkles, Layers, ShieldCheck, 
  X, Info, Check, ArrowRight, DollarSign, TrendingUp, AlertCircle,
  HelpCircle, UserCheck, SlidersHorizontal, ArrowUpRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import { 
  CompanyPriceListItem, 
  PriceListPayload,
  PriceListAnalytics
} from "../../services/priceListService";
import {
  usePriceListQuery,
  useCreatePriceListItemMutation,
  useUpdatePriceListItemMutation,
  useDeletePriceListItemMutation,
  useApprovePriceListItemMutation,
  useRejectPriceListItemMutation
} from "../../hooks/queries/usePriceListQueries";

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.03)] ${className}`}>
    {children}
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidth = "cht-max-w-xl" }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-absolute cht-inset-0 cht-bg-slate-950/50 cht-backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className={`cht-relative cht-w-full ${maxWidth} cht-bg-white cht-rounded-[32px] cht-p-7 cht-shadow-[0_25px_70px_rgba(0,0,0,0.15)] cht-max-h-[90vh] cht-overflow-y-auto cht-border cht-border-gray-100`}
          >
            <div className="cht-flex cht-justify-between cht-items-center cht-mb-5 cht-pb-3 cht-border-b cht-border-gray-100">
              <h2 className="cht-text-xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">{title}</h2>
              <button 
                onClick={onClose} 
                className="cht-p-2 cht-bg-gray-50 cht-rounded-xl cht-text-gray-400 hover:cht-text-gray-700 hover:cht-bg-gray-100 cht-transition-colors"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const DEFAULT_CATEGORIES = [
  "CAC & Corporate Affairs",
  "Software & Web Development",
  "Graphics, Branding & Media",
  "ICT & Professional Trainings",
  "Printing & Stationeries",
  "General Business Services"
];

const SuperAdminPriceList: React.FC = () => {
  const createMutation = useCreatePriceListItemMutation();
  const updateMutation = useUpdatePriceListItemMutation();
  const deleteMutation = useDeletePriceListItemMutation();
  const approveMutation = useApprovePriceListItemMutation();
  const rejectMutation = useRejectPriceListItemMutation();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [activeTab, setActiveTab] = useState<"catalog" | "queue">("catalog");

  // Stabilize query params for consistent cache keys across navigation
  const queryParams = useMemo(() => ({
    search: searchQuery.trim() || undefined,
    category: selectedCategory !== "ALL" ? selectedCategory : undefined,
  }), [searchQuery, selectedCategory]);

  const {
    data: priceListRes,
    isLoading: loading,
    isFetching: refreshing,
    refetch,
  } = usePriceListQuery(queryParams);

  const items = priceListRes?.data || [];
  const analytics = priceListRes?.analytics || null;
  const categories = analytics?.categories && analytics.categories.length > 0
    ? analytics.categories
    : DEFAULT_CATEGORIES;

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [activeItem, setActiveItem] = useState<CompanyPriceListItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Direct Form State
  const [formData, setFormData] = useState<PriceListPayload>({
    name: "",
    category: DEFAULT_CATEGORIES[0],
    standard_price: "",
    cost_price: "",
    wholesale_price: "",
    min_price: "",
    delivery_timeline: "3-5 Business Days",
    description: "",
    is_active: true,
  });

  const handleRefresh = () => {
    refetch();
  };

  // Pending queue items
  const queueItems = useMemo(() => {
    return items.filter((i) => i.status === "PENDING_APPROVAL" || i.change_status !== "NONE");
  }, [items]);

  // Catalog items
  const catalogItems = useMemo(() => {
    return items.filter((item) => {
      if (selectedCategory !== "ALL" && item.category !== selectedCategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = item.name.toLowerCase().includes(q);
        const matchCode = item.item_code.toLowerCase().includes(q);
        const matchCat = item.category.toLowerCase().includes(q);
        return matchName || matchCode || matchCat;
      }
      return true;
    });
  }, [items, selectedCategory, searchQuery]);

  // Open Direct Add Modal
  const handleOpenAddModal = () => {
    setFormData({
      name: "",
      category: categories[0] || DEFAULT_CATEGORIES[0],
      standard_price: "",
      cost_price: "",
      wholesale_price: "",
      min_price: "",
      delivery_timeline: "3-5 Business Days",
      description: "",
      is_active: true,
    });
    setFeedbackMsg(null);
    setIsAddModalOpen(true);
  };

  // Open Direct Edit Modal
  const handleOpenEditModal = (item: CompanyPriceListItem) => {
    setActiveItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      standard_price: item.standard_price,
      cost_price: item.cost_price,
      wholesale_price: item.wholesale_price || "",
      min_price: item.min_price || "",
      delivery_timeline: item.delivery_timeline || "",
      description: item.description || "",
      is_active: item.is_active,
    });
    setFeedbackMsg(null);
    setIsEditModalOpen(true);
  };

  // Direct Create Submission
  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.standard_price) {
      setFeedbackMsg({ type: "error", text: "Service name and standard price are required." });
      return;
    }

    try {
      setSubmitting(true);
      const res = await createMutation.mutateAsync(formData);
      setFeedbackMsg({ type: "success", text: res.message || "Price item created and published." });
      setTimeout(() => {
        setIsAddModalOpen(false);
      }, 1200);
    } catch (err: any) {
      console.error("Failed to create price item:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to create price item." });
    } finally {
      setSubmitting(false);
    }
  };

  // Direct Edit Submission
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeItem) return;

    try {
      setSubmitting(true);
      const res = await updateMutation.mutateAsync({ id: activeItem.id, payload: formData });
      setFeedbackMsg({ type: "success", text: res.message || "Price item updated successfully." });
      setTimeout(() => {
        setIsEditModalOpen(false);
      }, 1200);
    } catch (err: any) {
      console.error("Failed to update price item:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to update price item." });
    } finally {
      setSubmitting(false);
    }
  };

  // Direct Delete Submission
  const handleConfirmDirectDelete = async () => {
    if (!activeItem) return;

    try {
      setSubmitting(true);
      const res = await deleteMutation.mutateAsync(activeItem.id);
      setFeedbackMsg({ type: "success", text: res.message || res.detail || "Item deleted successfully." });
      setTimeout(() => {
        setIsDeleteModalOpen(false);
      }, 1200);
    } catch (err: any) {
      console.error("Failed to delete price item:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to delete price item." });
    } finally {
      setSubmitting(false);
    }
  };

  // SuperAdmin Approve Proposal
  const handleApproveProposal = async (item: CompanyPriceListItem) => {
    try {
      setSubmitting(true);
      const res = await approveMutation.mutateAsync(item.id);
      setFeedbackMsg({ type: "success", text: res.message || "Approved successfully." });
      setTimeout(() => {
        setIsReviewModalOpen(false);
      }, 1200);
    } catch (err: any) {
      console.error("Failed to approve proposal:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to approve proposal." });
    } finally {
      setSubmitting(false);
    }
  };

  // SuperAdmin Reject Proposal
  const handleRejectProposal = async (item: CompanyPriceListItem) => {
    try {
      setSubmitting(true);
      const res = await rejectMutation.mutateAsync({ id: item.id, reason: rejectionReason || "Rejected by SuperAdmin" });
      setFeedbackMsg({ type: "success", text: res.message || "Proposal rejected." });
      setTimeout(() => {
        setIsReviewModalOpen(false);
        setRejectionReason("");
      }, 1200);
    } catch (err: any) {
      console.error("Failed to reject proposal:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to reject proposal." });
    } finally {
      setSubmitting(false);
    }
  };

  const totalLive = analytics?.total_live_items ?? items.filter(i => i.status === "APPROVED" && i.is_active).length;
  const queueCount = analytics?.total_pending_queue ?? queueItems.length;

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-3 cht-py-1 cht-bg-indigo-50 cht-text-indigo-700 cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-rounded-xl">
              SuperAdmin Authority
            </span>
            <span className="cht-flex cht-items-center cht-gap-1 cht-text-xs cht-font-bold cht-text-gray-400">
              <ShieldCheck size={14} className="cht-text-indigo-600" /> Full CRUD & Approval Governance
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
            Company Price List Management
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Publish standard billing rates, manage wholesale partner pricing, review staff modification requests, and enforce profit margins.
          </p>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            leftIcon={<RefreshCw size={16} className={refreshing ? "cht-animate-spin" : ""} />}
            className="cht-rounded-xl"
            title="Refresh price list"
          >
            Refresh
          </Button>

          <Button
            variant="primary"
            onClick={handleOpenAddModal}
            leftIcon={<Plus size={16} />}
            className="cht-rounded-xl"
          >
            Add New Item
          </Button>
        </div>
      </header>

      {/* KPI Deck */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-5">
        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Live Offerings</span>
            <div className="cht-p-2.5 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-xl">
              <CheckCircle2 size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-2">
            {totalLive}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Active client billing services</p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Approval Queue</span>
            <div className={`cht-p-2.5 cht-rounded-xl ${queueCount > 0 ? "cht-bg-amber-100 cht-text-amber-800" : "cht-bg-gray-100 cht-text-gray-500"}`}>
              <Clock size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-amber-700 cht-mt-2">
            {queueCount}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">
            {analytics?.pending_additions || 0} additions, {analytics?.pending_updates || 0} edits, {analytics?.pending_deletions || 0} deletes
          </p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Service Categories</span>
            <div className="cht-p-2.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-xl">
              <Layers size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-2">
            {categories.length}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Service fulfillment lines</p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Profit Margin Guard</span>
            <div className="cht-p-2.5 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-xl">
              <TrendingUp size={18} />
            </div>
          </div>
          <h3 className="cht-text-base cht-font-black cht-text-indigo-900 cht-mt-2">
            Cost vs Billing Tracked
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Official fees & fulfillment costs</p>
        </GlassCard>
      </div>

      {/* Tabs & Search Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        {/* Tab Buttons */}
        <div className="cht-flex cht-items-center cht-gap-2 cht-p-1 cht-bg-gray-100/80 cht-rounded-2xl cht-w-full md:cht-w-auto">
          <button
            type="button"
            onClick={() => setActiveTab("catalog")}
            className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${
              activeTab === "catalog"
                ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                : "cht-text-gray-500 hover:cht-text-gray-800"
            }`}
          >
            Full Price Catalog ({catalogItems.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("queue")}
            className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-xs cht-font-black cht-flex cht-items-center cht-gap-1.5 cht-transition-all ${
              activeTab === "queue"
                ? "cht-bg-white cht-text-amber-700 cht-shadow-sm"
                : "cht-text-gray-500 hover:cht-text-gray-800"
            }`}
          >
            Pending Review Queue
            {queueCount > 0 && (
              <span className="cht-px-2 cht-py-0.5 cht-bg-amber-500 cht-text-white cht-text-[10px] cht-font-black cht-rounded-full">
                {queueCount}
              </span>
            )}
          </button>
        </div>

        {/* Search & Category Filter */}
        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          <div className="cht-relative cht-flex-1 md:cht-w-64">
            <Search size={16} className="cht-absolute cht-left-3 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" />
            <input
              type="text"
              placeholder="Search catalog..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="cht-w-full cht-pl-9 cht-pr-3 cht-py-2 cht-bg-gray-50 cht-border cht-border-gray-200/70 cht-rounded-xl cht-text-xs cht-font-medium focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-outline-none"
            />
          </div>

          <div className="cht-w-44">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="cht-w-full cht-py-2 cht-px-3 cht-bg-white cht-border cht-border-gray-200/70 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-outline-none"
            >
              <option value="ALL">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* TAB 1: Catalog View */}
      {activeTab === "catalog" && (
        <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
          <div className="cht-p-5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <div>
              <h4 className="cht-text-base cht-font-black cht-text-gray-900">
                Master Price List & Margin Insights
              </h4>
              <p className="cht-text-xs cht-font-medium cht-text-gray-400">
                SuperAdmin view includes cost prices, estimated margins, and wholesale agent rates.
              </p>
            </div>
            <span className="cht-text-xs cht-font-bold cht-text-gray-400">
              Showing {catalogItems.length} records
            </span>
          </div>

          <div className="cht-overflow-x-auto">
            <table className="cht-w-full cht-text-left cht-border-collapse">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  <th className="cht-py-4 cht-px-6">Code & Name</th>
                  <th className="cht-py-4 cht-px-6">Category</th>
                  <th className="cht-py-4 cht-px-6">Standard Price</th>
                  <th className="cht-py-4 cht-px-6">Cost / Fee</th>
                  <th className="cht-py-4 cht-px-6">Wholesale Price</th>
                  <th className="cht-py-4 cht-px-6">Floor Price</th>
                  <th className="cht-py-4 cht-px-6">Margin</th>
                  <th className="cht-py-4 cht-px-6">Status</th>
                  <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="cht-py-12 cht-text-center cht-text-gray-400">
                      <RefreshCw className="cht-animate-spin cht-mx-auto cht-mb-2 cht-text-[#3e57da]" size={24} />
                      <p className="cht-text-xs cht-font-bold">Loading price list...</p>
                    </td>
                  </tr>
                ) : catalogItems.length > 0 ? (
                  catalogItems.map((item) => (
                    <tr key={item.id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                      <td className="cht-py-4 cht-px-6">
                        <div className="cht-flex cht-items-start cht-gap-2">
                          <span className="cht-text-[10px] cht-font-mono cht-font-black cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-700 cht-rounded-md">
                            {item.item_code}
                          </span>
                          <div>
                            <p className="cht-text-xs cht-font-black cht-text-gray-900">{item.name}</p>
                            <p className="cht-text-[10px] cht-font-medium cht-text-gray-400">
                              Timeline: {item.delivery_timeline || "Standard"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-blue-50 cht-text-[#3e57da] cht-text-[11px] cht-font-black">
                          {item.category}
                        </span>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <span className="cht-text-xs cht-font-black cht-text-emerald-700">
                          ₦{Number(item.standard_price || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <span className="cht-text-xs cht-font-bold cht-text-rose-700">
                          ₦{Number(item.cost_price || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {item.wholesale_price ? (
                          <span className="cht-text-xs cht-font-bold cht-text-blue-700">
                            ₦{Number(item.wholesale_price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="cht-text-xs cht-text-gray-400">—</span>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {item.min_price ? (
                          <span className="cht-text-xs cht-font-bold cht-text-gray-600">
                            ₦{Number(item.min_price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="cht-text-xs cht-text-gray-400">—</span>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <span className="cht-text-xs cht-font-black cht-text-indigo-600">
                          ₦{Number(item.profit_margin || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {item.change_status === "PENDING_DELETE" ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200 cht-text-[10px] cht-font-black">
                            <AlertTriangle size={11} />
                            Deletion Staged
                          </span>
                        ) : item.change_status === "PENDING_UPDATE" ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200 cht-text-[10px] cht-font-black">
                            <Edit3 size={11} />
                            Edits Staged
                          </span>
                        ) : item.status === "PENDING_APPROVAL" ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200 cht-text-[10px] cht-font-black">
                            <Clock size={11} />
                            New Proposal
                          </span>
                        ) : item.status === "REJECTED" ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-gray-100 cht-text-gray-700 cht-text-[10px] cht-font-black">
                            <X size={11} />
                            Rejected
                          </span>
                        ) : (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-emerald-50 cht-text-emerald-700 cht-text-[10px] cht-font-black">
                            <CheckCircle2 size={11} />
                            Live
                          </span>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6 cht-text-right">
                        <div className="cht-flex cht-items-center cht-justify-end cht-gap-1.5">
                          {/* If item has pending review, show review button */}
                          {(item.status === "PENDING_APPROVAL" || item.change_status !== "NONE") && (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveItem(item);
                                setFeedbackMsg(null);
                                setIsReviewModalOpen(true);
                              }}
                              className="cht-p-1.5 cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-lg cht-transition-colors"
                              title="Review Proposal"
                            >
                              <ShieldCheck size={16} />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => {
                              setActiveItem(item);
                              setIsDetailModalOpen(true);
                            }}
                            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-blue-600 hover:cht-bg-blue-50 cht-rounded-lg cht-transition-colors"
                            title="View Details"
                          >
                            <Eye size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(item)}
                            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-[#3e57da] hover:cht-bg-blue-50 cht-rounded-lg cht-transition-colors"
                            title="Direct Edit"
                          >
                            <Edit3 size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setActiveItem(item);
                              setFeedbackMsg(null);
                              setIsDeleteModalOpen(true);
                            }}
                            className="cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-colors"
                            title="Direct Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="cht-py-12 cht-text-center cht-text-gray-400">
                      <Tag size={32} className="cht-mx-auto cht-mb-2 cht-text-gray-300" />
                      <p className="cht-text-sm cht-font-bold cht-text-gray-600">No services match filter criteria</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {/* TAB 2: Review Queue */}
      {activeTab === "queue" && (
        <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
          <div className="cht-p-5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <div>
              <h4 className="cht-text-base cht-font-black cht-text-gray-900">
                Staff Governance & Modification Requests ({queueItems.length})
              </h4>
              <p className="cht-text-xs cht-font-medium cht-text-gray-400">
                Review proposed additions, rate updates with side-by-side diffs, and deletion authorizations submitted by Admins.
              </p>
            </div>
          </div>

          <div className="cht-overflow-x-auto">
            <table className="cht-w-full cht-text-left cht-border-collapse">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  <th className="cht-py-4 cht-px-6">Proposal Type</th>
                  <th className="cht-py-4 cht-px-6">Item Code & Service</th>
                  <th className="cht-py-4 cht-px-6">Proposed By</th>
                  <th className="cht-py-4 cht-px-6">Current Live Value</th>
                  <th className="cht-py-4 cht-px-6">Proposed Value</th>
                  <th className="cht-py-4 cht-px-6 cht-text-right">Decision</th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {queueItems.length > 0 ? (
                  queueItems.map((item) => {
                    const isNew = item.status === "PENDING_APPROVAL";
                    const isUpdate = item.change_status === "PENDING_UPDATE";
                    const isDelete = item.change_status === "PENDING_DELETE";

                    return (
                      <tr key={item.id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                        <td className="cht-py-4 cht-px-6">
                          {isNew ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-amber-50 cht-text-amber-800 cht-text-[11px] cht-font-black">
                              <Plus size={12} /> New Addition
                            </span>
                          ) : isUpdate ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-indigo-50 cht-text-indigo-800 cht-text-[11px] cht-font-black">
                              <Edit3 size={12} /> Rate Edit
                            </span>
                          ) : (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-rose-50 cht-text-rose-800 cht-text-[11px] cht-font-black">
                              <Trash2 size={12} /> Deletion Request
                            </span>
                          )}
                        </td>

                        <td className="cht-py-4 cht-px-6">
                          <span className="cht-text-[10px] cht-font-mono cht-font-black cht-text-gray-500">
                            {item.item_code}
                          </span>
                          <h5 className="cht-text-xs cht-font-black cht-text-gray-900">{item.name}</h5>
                          <span className="cht-text-[10px] cht-text-[#3e57da] cht-font-bold">{item.category}</span>
                        </td>

                        <td className="cht-py-4 cht-px-6">
                          <span className="cht-text-xs cht-font-bold cht-text-gray-700">
                            {item.proposed_by_name || item.created_by_name || "Admin Staff"}
                          </span>
                        </td>

                        <td className="cht-py-4 cht-px-6">
                          {isNew ? (
                            <span className="cht-text-xs cht-text-gray-400">None (New)</span>
                          ) : (
                            <div>
                              <span className="cht-text-xs cht-font-bold cht-text-gray-800">
                                ₦{Number(item.standard_price || 0).toLocaleString("en-NG")}
                              </span>
                              <p className="cht-text-[10px] cht-text-gray-400">Timeline: {item.delivery_timeline || "Standard"}</p>
                            </div>
                          )}
                        </td>

                        <td className="cht-py-4 cht-px-6">
                          {isNew ? (
                            <div>
                              <span className="cht-text-xs cht-font-black cht-text-emerald-700">
                                ₦{Number(item.standard_price || 0).toLocaleString("en-NG")}
                              </span>
                              <p className="cht-text-[10px] cht-text-gray-500">Cost: ₦{Number(item.cost_price || 0).toLocaleString("en-NG")}</p>
                            </div>
                          ) : isUpdate && item.pending_changes ? (
                            <div>
                              {item.pending_changes.standard_price && (
                                <span className="cht-text-xs cht-font-black cht-text-indigo-700">
                                  Standard: ₦{Number(item.pending_changes.standard_price).toLocaleString("en-NG")}
                                </span>
                              )}
                              {item.pending_changes.wholesale_price && (
                                <p className="cht-text-[10px] cht-text-blue-700">
                                  Wholesale: ₦{Number(item.pending_changes.wholesale_price).toLocaleString("en-NG")}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="cht-text-xs cht-font-black cht-text-rose-700">Permanent Removal</span>
                          )}
                        </td>

                        <td className="cht-py-4 cht-px-6 cht-text-right">
                          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleApproveProposal(item)}
                              className="cht-rounded-lg cht-text-[11px] cht-py-1.5 cht-px-3"
                              leftIcon={<Check size={12} />}
                            >
                              Approve
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setActiveItem(item);
                                setFeedbackMsg(null);
                                setIsReviewModalOpen(true);
                              }}
                              className="cht-rounded-lg cht-text-[11px] cht-py-1.5 cht-px-3 cht-text-rose-600 hover:cht-bg-rose-50"
                              leftIcon={<X size={12} />}
                            >
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="cht-py-12 cht-text-center cht-text-gray-400">
                      <CheckCircle2 size={32} className="cht-mx-auto cht-mb-2 cht-text-emerald-500" />
                      <p className="cht-text-sm cht-font-black cht-text-gray-700">All proposals reviewed</p>
                      <p className="cht-text-xs cht-font-medium cht-text-gray-400 cht-mt-1">
                        There are currently no pending modifications awaiting SuperAdmin authorization.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {/* Direct Add / Create Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !submitting && setIsAddModalOpen(false)}
        title="Create & Publish New Price Item"
      >
        <form onSubmit={handleSubmitAdd} className="cht-space-y-4">
          <div className="cht-p-3 cht-bg-emerald-50/80 cht-border cht-border-emerald-200 cht-rounded-2xl cht-flex cht-items-start cht-gap-2.5">
            <CheckCircle2 size={16} className="cht-text-emerald-700 cht-shrink-0 cht-mt-0.5" />
            <p className="cht-text-[11px] cht-font-medium cht-text-emerald-800">
              SuperAdmin actions are published immediately to live billing and corporate catalogs.
            </p>
          </div>

          {feedbackMsg && (
            <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
              feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
            }`}>
              {feedbackMsg.text}
            </div>
          )}

          <Input
            label="Service / Product Name"
            required
            placeholder="e.g. CAC Business Name Registration"
            value={formData.name}
            onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              as="select"
              label="Category"
              required
              value={formData.category}
              onChange={(e: any) => setFormData({ ...formData, category: e.target.value })}
              options={categories.map((c) => ({ label: c, value: c }))}
            />

            <Input
              label="Turnaround Timeline"
              placeholder="e.g. 3-5 Business Days"
              value={formData.delivery_timeline || ""}
              onChange={(e: any) => setFormData({ ...formData, delivery_timeline: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Standard Retail Price"
              prefixText="₦"
              type="number"
              step="0.01"
              required
              placeholder="25000.00"
              value={formData.standard_price}
              onChange={(e: any) => setFormData({ ...formData, standard_price: e.target.value })}
            />

            <Input
              label="Direct Cost / Filing Fee"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="10000.00"
              value={formData.cost_price || ""}
              onChange={(e: any) => setFormData({ ...formData, cost_price: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Wholesale Partner Rate"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="20000.00"
              value={formData.wholesale_price || ""}
              onChange={(e: any) => setFormData({ ...formData, wholesale_price: e.target.value })}
            />

            <Input
              label="Minimum Floor Price"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="18000.00"
              value={formData.min_price || ""}
              onChange={(e: any) => setFormData({ ...formData, min_price: e.target.value })}
            />
          </div>

          <Input
            as="textarea"
            multiline
            rows={3}
            label="Description & Scope"
            placeholder="Detail what is covered in this service fee..."
            value={formData.description || ""}
            onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="cht-flex cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
              disabled={submitting}
              className="cht-rounded-xl"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Publishing..." : "Publish Live Item"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Direct Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !submitting && setIsEditModalOpen(false)}
        title={`Edit Master Item: ${activeItem?.item_code || ""}`}
      >
        <form onSubmit={handleSubmitEdit} className="cht-space-y-4">
          {feedbackMsg && (
            <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
              feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
            }`}>
              {feedbackMsg.text}
            </div>
          )}

          <Input
            label="Service Name"
            required
            value={formData.name}
            onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Standard Retail Price"
              prefixText="₦"
              type="number"
              step="0.01"
              required
              value={formData.standard_price}
              onChange={(e: any) => setFormData({ ...formData, standard_price: e.target.value })}
            />

            <Input
              label="Direct Cost / Fee"
              prefixText="₦"
              type="number"
              step="0.01"
              value={formData.cost_price || ""}
              onChange={(e: any) => setFormData({ ...formData, cost_price: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Wholesale Partner Rate"
              prefixText="₦"
              type="number"
              step="0.01"
              value={formData.wholesale_price || ""}
              onChange={(e: any) => setFormData({ ...formData, wholesale_price: e.target.value })}
            />

            <Input
              label="Minimum Floor Price"
              prefixText="₦"
              type="number"
              step="0.01"
              value={formData.min_price || ""}
              onChange={(e: any) => setFormData({ ...formData, min_price: e.target.value })}
            />
          </div>

          <Input
            as="textarea"
            multiline
            rows={3}
            label="Description & Scope"
            value={formData.description || ""}
            onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="cht-flex cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              disabled={submitting}
              className="cht-rounded-xl"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Saving..." : "Save Immediate Changes"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Review Proposal / Reject Reason Modal */}
      {activeItem && (
        <Modal
          isOpen={isReviewModalOpen}
          onClose={() => !submitting && setIsReviewModalOpen(false)}
          title={`Review Proposal: ${activeItem.name}`}
        >
          <div className="cht-space-y-4">
            <div className="cht-p-3.5 cht-bg-amber-50 cht-border cht-border-amber-200 cht-rounded-2xl cht-space-y-2">
              <span className="cht-text-[11px] cht-font-black cht-text-amber-900 cht-uppercase cht-tracking-wider">
                Proposal Summary
              </span>
              <div className="cht-text-xs cht-space-y-1">
                <p><strong className="cht-text-gray-700">Service:</strong> {activeItem.name} ({activeItem.item_code})</p>
                <p><strong className="cht-text-gray-700">Submitted By:</strong> {activeItem.proposed_by_name || activeItem.created_by_name || "Admin"}</p>
                {activeItem.change_status === "PENDING_UPDATE" && activeItem.pending_changes && (
                  <div className="cht-mt-2 cht-p-2 cht-bg-white cht-rounded-xl cht-border cht-border-amber-200">
                    <p className="cht-font-black cht-text-amber-800 cht-mb-1">Proposed Field Updates:</p>
                    {Object.entries(activeItem.pending_changes).map(([k, v]) => (
                      <div key={k} className="cht-flex cht-justify-between cht-text-[11px] cht-py-0.5">
                        <span className="cht-capitalize">{k.replace('_', ' ')}:</span>
                        <span className="cht-font-black cht-text-indigo-800">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {feedbackMsg && (
              <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
                feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
              }`}>
                {feedbackMsg.text}
              </div>
            )}

            <Input
              as="textarea"
              multiline
              rows={2}
              label="Rejection Reason (required if rejecting)"
              placeholder="State why this proposal is rejected..."
              value={rejectionReason}
              onChange={(e: any) => setRejectionReason(e.target.value)}
            />

            <div className="cht-flex cht-justify-end cht-gap-2.5 cht-pt-2">
              <Button
                variant="outline"
                onClick={() => setIsReviewModalOpen(false)}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                Close
              </Button>

              <Button
                variant="danger"
                onClick={() => handleRejectProposal(activeItem)}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                Reject Proposal
              </Button>

              <Button
                variant="primary"
                onClick={() => handleApproveProposal(activeItem)}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                Approve & Publish
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Direct Delete Modal */}
      {activeItem && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => !submitting && setIsDeleteModalOpen(false)}
          title="Delete Price Item"
        >
          <div className="cht-space-y-4">
            <div className="cht-p-4 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-2xl cht-space-y-2">
              <div className="cht-flex cht-items-center cht-gap-2 cht-text-rose-800 cht-font-black cht-text-sm">
                <AlertTriangle size={18} /> Confirm Permanent Removal
              </div>
              <p className="cht-text-xs cht-text-rose-700">
                Are you sure you want to delete <strong className="cht-font-black">{activeItem.name}</strong> ({activeItem.item_code})?
              </p>
              <p className="cht-text-[11px] cht-text-rose-600 cht-font-medium">
                This item will be permanently removed from all corporate billing catalogs.
              </p>
            </div>

            {feedbackMsg && (
              <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
                feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
              }`}>
                {feedbackMsg.text}
              </div>
            )}

            <div className="cht-flex cht-justify-end cht-gap-2.5">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDirectDelete}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                {submitting ? "Deleting..." : "Permanently Delete"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Details Modal */}
      {activeItem && (
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title={`Service Specification: ${activeItem.name}`}
        >
          <div className="cht-space-y-4">
            <div className="cht-bg-gray-50 cht-p-4 cht-rounded-2xl cht-space-y-2.5">
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Item Reference Code</span>
                <span className="cht-font-mono cht-font-black cht-text-gray-900">{activeItem.item_code}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Service Category</span>
                <span className="cht-font-black cht-text-[#3e57da]">{activeItem.category}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Turnaround Timeline</span>
                <span className="cht-font-bold cht-text-gray-700">{activeItem.delivery_timeline || "N/A"}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Created By</span>
                <span className="cht-font-bold cht-text-gray-700">{activeItem.created_by_name || "Admin"}</span>
              </div>
              {activeItem.approved_by_name && (
                <div className="cht-flex cht-justify-between cht-text-xs">
                  <span className="cht-text-gray-400 cht-font-bold">Approved By</span>
                  <span className="cht-font-bold cht-text-emerald-700">{activeItem.approved_by_name}</span>
                </div>
              )}
            </div>

            {/* Pricing Matrix */}
            <div className="cht-grid cht-grid-cols-4 cht-gap-2.5 cht-text-center">
              <div className="cht-p-3 cht-bg-emerald-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-emerald-800 cht-uppercase">Standard</span>
                <p className="cht-text-sm cht-font-black cht-text-emerald-700 cht-mt-1">
                  ₦{Number(activeItem.standard_price || 0).toLocaleString("en-NG")}
                </p>
              </div>

              <div className="cht-p-3 cht-bg-rose-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-rose-800 cht-uppercase">Cost Fee</span>
                <p className="cht-text-sm cht-font-black cht-text-rose-700 cht-mt-1">
                  ₦{Number(activeItem.cost_price || 0).toLocaleString("en-NG")}
                </p>
              </div>

              <div className="cht-p-3 cht-bg-blue-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase">Wholesale</span>
                <p className="cht-text-sm cht-font-black cht-text-blue-700 cht-mt-1">
                  {activeItem.wholesale_price ? `₦${Number(activeItem.wholesale_price).toLocaleString("en-NG")}` : "N/A"}
                </p>
              </div>

              <div className="cht-p-3 cht-bg-indigo-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-indigo-800 cht-uppercase">Net Margin</span>
                <p className="cht-text-sm cht-font-black cht-text-indigo-700 cht-mt-1">
                  ₦{Number(activeItem.profit_margin || 0).toLocaleString("en-NG")}
                </p>
              </div>
            </div>

            {/* Description */}
            {activeItem.description && (
              <div className="cht-p-3.5 cht-border cht-border-gray-100 cht-rounded-2xl">
                <h5 className="cht-text-xs cht-font-black cht-text-gray-800 cht-mb-1">Fulfillment Deliverables</h5>
                <p className="cht-text-xs cht-text-gray-600 cht-leading-relaxed">{activeItem.description}</p>
              </div>
            )}

            <div className="cht-flex cht-justify-end cht-pt-2">
              <Button
                variant="outline"
                onClick={() => setIsDetailModalOpen(false)}
                className="cht-rounded-xl"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SuperAdminPriceList;
